import { apiClient } from './api.client';

/**
 * D1 — Identity-Review ops console (BRD §4). When a 2nd-SP introduction matches an existing investor
 * by email but the introduced NAME conflicts, the link is parked as IDENTITY_REVIEW (never auto-merged)
 * and is invisible to the investor until a compliance operator resolves it here:
 *   clear  → PENDING  (same person; the investor then confirms at sign-in)
 *   reject → DECLINED (different person; no link created)
 */
export interface IdentityReviewDto {
  id: number;
  status: string;
  matchedBy?: string;
  relationshipType?: string;
  accessLevel?: string;
  /** The existing (anchor) investor the introduction matched by email. */
  anchorInvestorId?: number;
  investorName?: string;
  investorEmail?: string;
  investorUniqueCode?: string;
  /** The new introduction under review. */
  introCode?: string;
  productName?: string;
  serviceProviderName?: string;
  relatedRef?: string;
  reviewNote?: string;
  createdAt?: string;
  confirmedAt?: string;
  declinedAt?: string;
}

class IdentityReviewService {
  private baseUrl = '/api/admin/identity-reviews';

  /** Ops queue — defaults to IDENTITY_REVIEW; pass a status to view others (PENDING/ACTIVE/DECLINED). */
  async list(status?: string): Promise<IdentityReviewDto[]> {
    const url = status ? `${this.baseUrl}?status=${encodeURIComponent(status)}` : this.baseUrl;
    const res = await apiClient.get<IdentityReviewDto[]>(url);
    return res.data ?? [];
  }

  /** Clear (verified same person) → PENDING; the investor confirms the link on next sign-in. */
  async clear(id: number, note?: string): Promise<IdentityReviewDto> {
    const res = await apiClient.post<IdentityReviewDto>(`${this.baseUrl}/${id}/clear`, note ? { note } : {});
    return res.data;
  }

  /** Reject (different person / genuine conflict) → DECLINED; no link is created. */
  async reject(id: number, note?: string): Promise<IdentityReviewDto> {
    const res = await apiClient.post<IdentityReviewDto>(`${this.baseUrl}/${id}/reject`, note ? { note } : {});
    return res.data;
  }
}

export const identityReviewService = new IdentityReviewService();
